import { CSSProperties, ReactElement } from "react";

import DotsIcon from "@uui/AnnotationDots";
import FrownFaceIcon from "@uui/FaceFrown";
import SmileFaceIcon from "@uui/FaceSmile";
import CheckIcon from "@uui/MessageCheckSquare";
import QuestionIcon from "@uui/MessageQuestionSquare";

import { Theme } from "@mui/material";
import Chip from "@mui/material/Chip";
import styled from "@mui/material/styles/styled";

import type { HighlightClipType } from "@app/common/types";

import IconBackground from "@rui/IconBackground";

export interface HighlightChipProps {
  type: HighlightClipType;
}

interface ChipProps {
  label: string;
  backgroundColor: (theme: Theme) => CSSProperties["backgroundColor"];
  color: (theme: Theme) => CSSProperties["backgroundColor"];
  icon: ReactElement;
}

const PROPS: { [key in HighlightChipProps["type"]]: ChipProps } = {
  positive: {
    label: "Reaction",
    color: (theme) => theme.palette.shades.meadow[600],
    backgroundColor: (theme) => theme.palette.shades.meadow[100],
    icon: <SmileFaceIcon />,
  },
  negative: {
    label: "Reaction",
    color: (theme) => theme.palette.shades.negative[500],
    backgroundColor: (theme) => theme.palette.shades.negative[100],
    icon: <FrownFaceIcon />,
  },
  topic: {
    label: "Topic",
    color: () => "#25B3CC",
    backgroundColor: () => "#C0ECF3",
    icon: <DotsIcon />,
  },
  action_item: {
    label: "Action Item",
    color: (theme) => theme.palette.shades.primary[400],
    backgroundColor: (theme) => theme.palette.shades.primary[100],
    icon: <CheckIcon />,
  },
  key_question: {
    label: "Key Question",
    color: () => "#D98C4C",
    backgroundColor: () => "#FCEDDC",
    icon: <QuestionIcon />,
  },
};

const HighlightChip = styled(({ type, ...props }: HighlightChipProps) => (
  <Chip
    icon={
      <IconBackground height={16} sx={{ bgcolor: "rgba(255, 255, 255, 0.4)" }}>
        {PROPS[type].icon}
      </IconBackground>
    }
    label={PROPS[type].label}
    {...props}
  />
))(({ theme, type }) => ({
  color: PROPS[type].color(theme),
  fontSize: "0.75rem",
  fontWeight: 500,
  lineHeight: "16px",
  letterSpacing: "-0.01rem",
  height: "20px",
  background: PROPS[type].backgroundColor(theme),
  borderRadius: "10px 0px 8px 10px",
  "& .MuiChip-label": {
    padding: "4px",
  },
  "& .MuiChip-icon": {
    marginLeft: "2px",
    marginRight: "0px",
  },
  "& .MuiChip-icon svg": {
    color: PROPS[type].color(theme),
    width: "12px",
    height: "12px",
  },
}));

export default HighlightChip;
